import { h, Component } from 'preact'
// @ts-expect-error   // fix this by defining the mapping to 'downshift' in tsconfig
import Downshift from 'downshift/preact'
import { SearchBoxProvided } from 'react-instantsearch/connectors'
import { insights, AnswerHit, AlgoliaAnswerCallback } from '../../../services/algoliaService'
import HitContainerWithFederated, { clickableLink } from './HitContainerWithFederated'
import { AllSearchResults, SearchResults, SearchResult, SearchResultWithMetadata, Hit, ZendeskHit, EventHit, isCommunityResultEvent, SalesforceHit, SkilljarHit, FreshdeskHit, SearchType, TopicClickedEvent, TopicClickedGoogleAnalyticsEvent, isCommunityResult, FederatedHit } from './index'
import { SearchBox, doSearch } from './SearchBox'
import localStorageCache from '../../../utils/localStorageCache'
import { SEARCH_CONVERSION } from '../../../libs/config'
import { SearchQuery } from './Search'
import { ControllerStateAndHelpers } from 'downshift'
import tracker from '../../../components/tracker/publish'
import Analytics from '../../analytics/Analytics'

interface Props {
  refine: SearchBoxProvided['refine']
  updateSearch?: (query: SearchQuery, cb: () => void) => void
  allSearchResults: SearchResults | AllSearchResults | null
  indexName: string
  getAnswers: (input: string, cb: AlgoliaAnswerCallback) => void
  enableFocusToSearch?: boolean
  callbackOnOutsideClick?: () => void
}

const eventTrackerInfo = (hit: AnswerHit|EventHit|Hit|ZendeskHit|SalesforceHit|SkilljarHit|FreshdeskHit|FederatedHit, type: SearchType, idx: number) => {
  let resultType = 'Yip.CommunityResultClicked'
  if (type === 'zendesk') {
    resultType = 'Yip.ZendeskGuideResultClicked'
  }
  if (type === 'salesforce') {
    resultType = 'Yip.SalesforceKBResultClicked'
  }
  if (type === 'skilljar') {
    resultType = 'Yip.SkilljarResultClicked'
  }
  if (type === 'freshdesk') {
    resultType = 'Yip.FreshdeskResultClicked'
  }
  if (type === 'federated') {
    resultType = 'Yip.FedereatedResultClicked'
  }

  const track: TopicClickedEvent = {
    type: resultType,
    topic_url: hit.topic_url ?? '',
    click_source: 'live-search',
    topic_position: idx,
    topic_title: hit.title,
    topic_topic_type: isCommunityResult(hit) ? hit.content_type : type
  }
  if (isCommunityResult(hit) && !isCommunityResultEvent(hit)) {
    track.category_id = hit.forum
    track.category_title = hit.forum_title
  }

  const gaEvents: TopicClickedGoogleAnalyticsEvent = {
    eventCategory: 'Live Search',
    eventAction: 'Topic Clicked',
    eventLabel: {
      Link: hit.topic_url ?? '',
      Position: idx,
      Type: isCommunityResult(hit) ? hit.content_type : type
    }
  }

  return {
    track,
    gaEvents
  }
}

class AlgoliaSearch extends Component<Props> {
  itemToString (i: SearchResult) {
    return i ? i.title : i
  }

  stateReducer (state: {inputValue: string}, changes: {type: string}) {
    switch (changes.type) {
      // prevent closing of the popup on certain actions
      case Downshift.stateChangeTypes.mouseUp:
      case Downshift.stateChangeTypes.touchStart:
        return {}
        // prevent clicking/selecting items from overwriting inputValue
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem:
        return {
          ...changes,
          inputValue: state.inputValue,
          highlightedIndex: null
        }
      case Downshift.stateChangeTypes.blurInput:
      case Downshift.stateChangeTypes.keyDownEscape: {
        if (this.props.callbackOnOutsideClick !== undefined && typeof this.props.callbackOnOutsideClick === 'function') {
          this.props.callbackOnOutsideClick()
        }
        return {
          ...changes,
          inputValue: state.inputValue,
          highlightedIndex: null
        }
      }
      default:
        return changes
    }
  }

  handleOnChange (item: SearchResultWithMetadata) {
    function isSimpleUrl (item: SearchResultWithMetadata | clickableLink) {
      return !('objectID' in item)
    }

    if (item === null) {
      return
    }

    if (isSimpleUrl(item)) {
      window.location.href = item.url ?? ''
    }

    insights('clickedObjectIDsAfterSearch', {
      objectIDs: [item.objectID],
      eventName: 'Live search item click',
      positions: [item.position],
      queryID: item.queryID,
      index: item.indexName
    })

    let resultType: SearchType = 'community'
    const type = item.indexName.split('-').pop()
    if (type === 'zendesk') resultType = 'zendesk'
    if (type === 'salesforce') resultType = 'salesforce'
    if (type === 'skilljar') resultType = 'skilljar'
    if (type === 'freshdesk') resultType = 'freshdesk'
    if (type === 'federated') resultType = 'federated'

    const { track, gaEvents } = eventTrackerInfo(item, resultType, item.position)

    tracker.trackEvent('userevent', track)
    Analytics.send(gaEvents.eventCategory, gaEvents.eventAction, gaEvents.eventLabel)

    localStorageCache.setItem(SEARCH_CONVERSION, {
      objectIDs: [item.objectID],
      queryID: item.queryID,
      index: item.indexName
    })

    // user pressed enter to select the item
    if ('KeyboardEvent' in window && window.event instanceof window.KeyboardEvent) {
      if (/^https?/.test(item.topic_url ?? '')) {
        window.open(item.topic_url, '_blank')
      } else {
        window.location.href = item.topic_url ?? item.url ?? ''
      }
    }
  }

  render ({
    refine,
    updateSearch,
    allSearchResults,
    indexName,
    enableFocusToSearch,
    callbackOnOutsideClick
  }: Props) {
    return (
      <Downshift
        itemToString={this.itemToString}
        stateReducer={this.stateReducer.bind(this)}
        onChange={this.handleOnChange}
      >
        {({
          getInputProps,
          getItemProps,
          highlightedIndex,
          setHighlightedIndex,
          closeMenu,
          openMenu,
          inputValue,
          reset,
          clearSelection,
          isOpen
        }: ControllerStateAndHelpers<SearchResult>) => {
          return (
            <div className='algolia-search-container'>

              <div className={`${!inputValue ? 'algolia-search-container--empty-input' : ''}`}>
                {
              isOpen
                ? <div
                    key='backdrop' onClick={() => {
                      closeMenu()

                      if (callbackOnOutsideClick !== undefined && typeof callbackOnOutsideClick === 'function') {
                        callbackOnOutsideClick()
                      }
                    }} className='algolia-search--opacity'
                  />
                : null
            }
                <SearchBox
                  reset={reset}
                  clearSelection={clearSelection}
                  refine={refine}
                  updateSearch={updateSearch}
                  setHighlightedIndex={setHighlightedIndex}
                  inputValue={inputValue ?? ''}
                  getInputProps={getInputProps}
                  openMenu={openMenu}
                  isOpen={isOpen}
                  enableFocusToSearch={enableFocusToSearch}
                  key='search'
                />

                {inputValue && isOpen
                  ? <HitContainerWithFederated
                      key='hits-container'
                      allSearchResults={allSearchResults}
                      inputValue={inputValue}
                      doSearch={doSearch}
                      highlightedIndex={highlightedIndex}
                      showFooterLinks
                      setHighlightedIndex={setHighlightedIndex}
                      getItemProps={getItemProps}
                      indexName={indexName}
                    />
                  : null}
              </div>
            </div>
          )
        }}
      </Downshift>
    )
  }
}

export default AlgoliaSearch
